import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, TextField, Button, Paper, Alert, Grid, CircularProgress, Table, TableHead, TableBody, TableRow, TableCell, Snackbar, LinearProgress } from '@mui/material';
import { doc, setDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { trackBandwidthUsage, getBandwidthUsagePercent, isBandwidthLimitReached } from '../utils/bandwidthTracker';
import { ref, uploadBytes, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { PhotoCamera } from '@mui/icons-material';
import exifr from 'exifr/dist/lite.umd';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '../contexts/AuthContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import piexif from 'piexifjs';
import PageLayout from '../components/PageLayout';
import PageTitle from '../components/PageTitle';
import OrphanedPhotosDates from '../components/OrphanedPhotosDates';

// Déclaration pour TypeScript
declare global {
  interface Window {
    JSZip: any;
  }
}

function base64ToBlob(base64: string, mime: string) {
  const byteString = atob(base64.split(',')[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mime });
}

// Page d'import d'URL de photos avec récupération automatique de la date EXIF
const PhotoImport: React.FC = () => {
  const { user } = useAuth();
  const [files, setFiles] = useState<File[]>([]);
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string|null>(null);
  const [successMessage, setSuccessMessage] = useState<string|null>(null);
  const [uploadProgress, setUploadProgress] = useState<{[key: number]: number}>({});
  const [bandwidthUsage, setBandwidthUsage] = useState<number>(0);

  // Fonction utilitaire pour récupérer la date EXIF d'une image locale (plusieurs champs)
  const fetchExifDateFromFile = async (file: File): Promise<{date: string|null, field: string|null, exif?: any}> => {
    try {
      const exif = await exifr.parse(file);
      if (exif) {
        if (exif.DateTimeOriginal) {
          return { date: exif.DateTimeOriginal.toISOString(), field: 'DateTimeOriginal', exif };
        }
        if (exif.CreateDate) {
          return { date: exif.CreateDate.toISOString(), field: 'CreateDate', exif };
        }
        if (exif.ModifyDate) {
          return { date: exif.ModifyDate.toISOString(), field: 'ModifyDate', exif };
        }
        return { date: null, field: null, exif };
      }
    } catch (e) {
      return { date: null, field: null };
    }
    return { date: null, field: null };
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setResults([]);
    const fileList = Array.from(e.target.files || []);
    setFiles(fileList);
    if (fileList.length === 0) return;
    setLoading(true);
    const imported: any[] = [];
    for (const file of fileList) {
      let date: string|null = null;
      let field: string|null = null;
      let exif: any = null;
      let compressedUrl: string|null = null;
      let compressedBlob: Blob|null = null;
      let compressedSize: number|null = null;
      let manualDate: Date | null = null;
      try {
        const res = await fetchExifDateFromFile(file);
        date = res.date;
        field = res.field;
        exif = res.exif;
      } catch (e) {}
      const url = URL.createObjectURL(file);
      // Création de miniatures optimisées pour le stockage et l'affichage
      const compressImage = async (file: File): Promise<{blob: Blob, url: string, size: number}> => {
        return new Promise((resolve, reject) => {
          const img = new window.Image();
          img.onload = () => {
            // Calculer les dimensions optimales pour une miniature de qualité
            // Taille max de 1000px (compromis entre qualité et économie de bande passante)
            const MAX_SIZE = 1000;
            let width = img.width;
            let height = img.height;
            
            // Redimensionner proportionnellement si l'image est trop grande
            if (width > MAX_SIZE || height > MAX_SIZE) {
              if (width > height) {
                height = Math.round(height * (MAX_SIZE / width));
                width = MAX_SIZE;
              } else {
                width = Math.round(width * (MAX_SIZE / height));
                height = MAX_SIZE;
              }
            }
            
            // Réduire encore plus si l'image est très grande à l'origine (pour optimiser davantage)
            const VERY_LARGE = 3000;
            if (img.width > VERY_LARGE || img.height > VERY_LARGE) {
              // Réduire encore de 20% pour les très grandes images
              width = Math.round(width * 0.8);
              height = Math.round(height * 0.8);
            }
            
            // Créer le canvas avec les nouvelles dimensions
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) return reject('Canvas error');
            
            // Dessiner l'image redimensionnée avec antialiasing
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, width, height);
            
            // Compression avec qualité adaptative pour atteindre une taille cible
            // Objectif : 200-250ko pour un bon équilibre entre qualité et économie de bande passante
            let quality = 0.82; // Qualité initiale intermédiaire pour les miniatures
            const TARGET_SIZE = 250 * 1024; // 250ko
            
            const tryCompress = () => {
              canvas.toBlob(blob => {
                if (!blob) return reject('Blob error');
                // Accepter si la taille est correcte ou si on a atteint la limite de compression
                if (blob.size <= TARGET_SIZE || quality <= 0.5) {
                  console.log(`Image compressée: ${(blob.size/1024).toFixed(1)}ko avec qualité ${quality.toFixed(2)}`);
                  const url = URL.createObjectURL(blob);
                  resolve({ blob, url, size: blob.size });
                } else {
                  // Réduire la qualité progressivement
                  quality -= 0.05;
                  tryCompress();
                }
              }, 'image/jpeg', quality);
            };
            tryCompress();
          };
          img.onerror = reject;
          img.src = url;
        });
      };
      let error = null;
      try {
        const { blob, url: compUrl, size } = await compressImage(file);
        // Injection EXIF : DateTimeOriginal (date détectée OU manuelle)
        let finalBlob = blob;
        let finalUrl = compUrl;
        // Utilise la date détectée, sinon la date manuelle
        let dateToUse = date;
        if (!dateToUse && manualDate) {
          dateToUse = manualDate;
        }
        if (dateToUse) {
          try {
            // Lire le blob compressé en base64
            const arrayBuffer = await blob.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);
            let binary = '';
            for (let i = 0; i < uint8Array.length; i++) binary += String.fromCharCode(uint8Array[i]);
            let jpegData = btoa(binary);
            // Générer l'EXIF à injecter
            const exifObj = { '0th': {}, Exif: {} };
            // DateTimeOriginal au format "YYYY:MM:DD HH:MM:SS"
            const d = new Date(dateToUse);
            const pad = (n: number) => n.toString().padStart(2, '0');
            const exifDate = `${d.getFullYear()}:${pad(d.getMonth()+1)}:${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
            // Injecte dans tous les champs pertinents
            (exifObj.Exif as any)[(piexif.ExifIFD as any).DateTimeOriginal] = exifDate;
            (exifObj.Exif as any)[(piexif.ExifIFD as any).CreateDate] = exifDate;
            (exifObj['0th'] as any)[(piexif.ImageIFD as any).DateTime] = exifDate;
            // Ajoute SubSecTimeOriginal pour la compatibilité exifr
            (exifObj.Exif as any)[(piexif.ExifIFD as any).SubSecTimeOriginal] = "0";
            // Ajoute DateTimeDigitized pour compatibilité Windows
            (exifObj['0th'] as any)[(piexif.ImageIFD as any).DateTimeDigitized] = exifDate;
            const exifBytes = piexif.dump(exifObj);
            // Injecter l'EXIF dans le JPEG
            const jpegWithExif = piexif.insert(exifBytes, "data:image/jpeg;base64," + jpegData);
            finalBlob = base64ToBlob(jpegWithExif, 'image/jpeg');
            finalUrl = URL.createObjectURL(finalBlob);
          } catch (ex) {
            error = 'EXIF: ' + (ex instanceof Error ? ex.message : String(ex));
          }
        }
        compressedUrl = finalUrl;
        compressedBlob = finalBlob;
        compressedSize = finalBlob.size;
      } catch (e) {
        error = 'Compression: ' + (e instanceof Error ? e.message : String(e));
        compressedUrl = null;
        compressedBlob = null;
        compressedSize = null;
      }
      imported.push({ file, url, date, field, exif, manualDate, compressedUrl, compressedBlob, compressedSize, error });
    }
    setResults(imported);
    setLoading(false);
  };

  // Vérifier l'utilisation de la bande passante au chargement du composant
  useEffect(() => {
    const checkBandwidthUsage = async () => {
      try {
        const usagePercent = await getBandwidthUsagePercent();
        setBandwidthUsage(usagePercent);
      } catch (error) {
        console.error('Erreur lors de la vérification de l\'utilisation de la bande passante:', error);
      }
    };
    
    checkBandwidthUsage();
    // Vérifier toutes les 5 minutes
    const interval = setInterval(checkBandwidthUsage, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Fonction pour uploader les images vers Firebase Storage
  const handleUpload = async () => {
    if (!user) {
      setError("Vous devez être connecté pour uploader des photos");
      return;
    }
    
    if (results.length === 0) {
      setError("Aucune image à uploader");
      return;
    }

    setUploading(true);
    setError(null);
    setSuccessMessage(null);
    
    const uploadedImages: any[] = [];
    const newUploadProgress = {...uploadProgress};
    
    try {
      console.log("Début de l'upload...");
      console.log("Configuration Firebase Storage:", storage);
      
      // Vérifier la limite de bande passante avant de commencer
      const isLimitReached = await isBandwidthLimitReached();
      if (isLimitReached) {
        setError("La limite quotidienne de bande passante a été atteinte. Veuillez réessayer demain.");
        setUploading(false);
        return;
      }
      
      for (let i = 0; i < results.length; i++) {
        const result = results[i];
        if (!result.compressedBlob || !result.date) {
          console.log(`Image ${i} ignorée: pas de blob ou de date`);
          continue; // Skip images without compression or date
        }
        
        // Generate unique filename
        const timestamp = new Date().getTime();
        const originalName = result.file.name.replace(/\.[^.]+$/, '');
        const uniqueFilename = `${originalName}_${timestamp}.jpg`;
        
        // Create storage reference
        const photoDate = new Date(result.date);
        const year = photoDate.getFullYear();
        const month = (photoDate.getMonth() + 1).toString().padStart(2, '0');
        
        // Path format: photos/YYYY/MM/filename.jpg
        const storagePath = `photos/${year}/${month}/${uniqueFilename}`;
        console.log(`Chemin de stockage: ${storagePath}`);
        
        try {
          // Création de la référence de stockage
          console.log("Création de la référence de stockage...");
          const storageRef = ref(storage, storagePath);
          console.log("Référence créée:", storageRef);
          
          // Upload file with progress monitoring
          newUploadProgress[i] = 5; // Initialisation
          setUploadProgress({...newUploadProgress});
          console.log(`Début upload image ${i}`);
          
          // Utiliser uploadBytesResumable pour un upload avec progression
          console.log(`Taille du blob: ${result.compressedSize} octets`);
          console.log(`Type du blob: ${result.compressedBlob.type}`);
          
          // Convertir le blob en File si nécessaire pour éviter les problèmes de type
          console.log("Conversion du blob en File...");
          const fileToUpload = new File(
            [result.compressedBlob], 
            `${uniqueFilename}`, 
            { type: 'image/jpeg' }
          );
          console.log("File créé:", fileToUpload.name, fileToUpload.size, fileToUpload.type);
          
          // Utiliser uploadBytesResumable pour suivre la progression
          console.log("Démarrage de l'upload avec uploadBytesResumable...");
          const uploadTask = uploadBytesResumable(storageRef, fileToUpload);
          console.log("Tâche d'upload créée");
          
          // Attendre la fin de l'upload avec une promesse
          console.log("Configuration des écouteurs d'événements...");
          await new Promise((resolve, reject) => {
            uploadTask.on(
              'state_changed',
              (snapshot) => {
                // Calculer et afficher la progression
                const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 45) + 5;
                console.log(`Progression de l'upload: ${snapshot.bytesTransferred}/${snapshot.totalBytes} (${progress}%)`);
                newUploadProgress[i] = progress;
                setUploadProgress({...newUploadProgress});
              },
              (error) => {
                // Gérer les erreurs
                console.error(`Erreur lors de l'upload de l'image ${i}:`, error);
                console.error("Code d'erreur:", error.code);
                console.error("Message d'erreur:", error.message);
                console.error("Détails de l'erreur:", JSON.stringify(error));
                reject(error);
              },
              () => {
                // Upload terminé avec succès
                console.log(`Upload réussi pour l'image ${i}`);
                newUploadProgress[i] = 50;
                setUploadProgress({...newUploadProgress});
                resolve(uploadTask.snapshot);
              }
            );
          });
          
          // Get download URL
          console.log(`Récupération de l'URL pour l'image ${i}`);
          let downloadURL;
          try {
            downloadURL = await getDownloadURL(storageRef);
            console.log(`URL obtenue: ${downloadURL.substring(0, 50)}...`);
          } catch (urlError) {
            console.error(`Erreur lors de la récupération de l'URL pour l'image ${i}:`, urlError);
            throw urlError;
          }
          
          newUploadProgress[i] = 75; // Got URL
          setUploadProgress({...newUploadProgress});
          
          // Suivre la consommation de bande passante pour l'upload
          console.log(`Suivi de la bande passante: ${result.compressedSize} octets`);
          await trackBandwidthUsage(result.compressedSize || 0);
          // Mettre à jour l'affichage de l'utilisation
          const usagePercent = await getBandwidthUsagePercent();
          console.log(`Utilisation de la bande passante: ${usagePercent.toFixed(2)}%`);
          setBandwidthUsage(usagePercent);
          
          // Save metadata to Firestore
          console.log(`Sauvegarde des métadonnées dans Firestore pour l'image ${i}`);
          const photoDoc = {
            name: uniqueFilename,
            originalName: result.file.name,
            path: storagePath,
            url: downloadURL,
            size: result.compressedSize,
            date: new Date(result.date),
            uploadedBy: user.uid,
            uploadedAt: serverTimestamp(),
            exifField: result.field || 'manual'
          };
          
          let docRef;
          try {
            docRef = await addDoc(collection(db, 'photos'), photoDoc);
            console.log(`Document Firestore créé avec ID: ${docRef.id}`);
          } catch (firestoreError) {
            console.error(`Erreur lors de l'ajout du document Firestore pour l'image ${i}:`, firestoreError);
            throw firestoreError;
          }
          
          newUploadProgress[i] = 100; // Completed
          setUploadProgress({...newUploadProgress});
          
          uploadedImages.push({
            ...photoDoc,
            id: docRef.id
          });
        } catch (uploadError) {
          console.error(`Erreur lors du traitement de l'image ${i}:`, uploadError);
          console.error("Détails de l'erreur:", uploadError instanceof Error ? uploadError.stack : String(uploadError));
          setError(`Erreur lors du traitement de l'image ${result.file.name}: ${uploadError instanceof Error ? uploadError.message : String(uploadError)}`);
          break; // Arrêter le traitement des images en cas d'erreur
        }
      }
      
      if (uploadedImages.length > 0) {
        setSuccessMessage(`${uploadedImages.length} photo(s) uploadée(s) avec succès !`);
        console.log(`Upload terminé: ${uploadedImages.length} photo(s) uploadée(s)`);
      } else {
        setError("Aucune photo n'a pu être uploadée. Vérifiez les permissions Firebase et la connexion internet.");
        console.error("Aucune photo uploadée avec succès");
      }
    } catch (err) {
      console.error("Erreur générale lors de l'upload:", err);
      console.error("Détails de l'erreur générale:", err instanceof Error ? err.stack : String(err));
      setError(`Erreur lors de l'upload : ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setUploading(false);
      console.log("Fin du processus d'upload");
    }
  };

  return (
    <PageLayout>
      <PageTitle
        title="Importation de photos"
        icon={<PhotoCamera />}
      />
      <Paper sx={{ p: 3 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Sélectionnez une ou plusieurs photos. La date de prise de vue sera lue automatiquement (EXIF), si possible.
          Les images seront compressées à moins de 500 Ko avant l'upload.
        </Typography>
        <Button
          variant="contained"
          component="label"
          sx={{ mb: 2, mr: 2 }}
          disabled={uploading}
        >
          Choisir des photos
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={handleFileChange}
          />
        </Button>
        
        {results.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="success"
              sx={{ mb: 2 }}
              onClick={handleUpload}
              disabled={uploading || loading || results.length === 0}
            >
              Uploader les photos ({results.length})
            </Button>
            <Button
              variant="outlined"
              color="info"
              sx={{ mb: 2 }}
              onClick={() => {
                // Créer un zip avec toutes les images compressées pour téléchargement
                const downloadAllImages = async () => {
                  try {
                    // Vérifier si JSZip est disponible
                    if (typeof window.JSZip === 'undefined') {
                      // Charger dynamiquement JSZip si nécessaire
                      const script = document.createElement('script');
                      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
                      document.body.appendChild(script);
                      
                      // Attendre que le script soit chargé
                      await new Promise((resolve) => {
                        script.onload = resolve;
                      });
                    }
                    
                    // Créer un nouveau zip
                    const JSZip = window.JSZip;
                    const zip = new JSZip();
                    
                    // Ajouter chaque image au zip
                    for (let i = 0; i < results.length; i++) {
                      const result = results[i];
                      if (result.compressedBlob) {
                        const filename = `${result.file.name.replace(/\.[^.]+$/, '.jpg')}`;
                        zip.file(filename, result.compressedBlob);
                      }
                    }
                    
                    // Générer le zip
                    const zipBlob = await zip.generateAsync({ type: 'blob' });
                    
                    // Créer un lien de téléchargement
                    const url = URL.createObjectURL(zipBlob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `rezone_photos_${new Date().toISOString().slice(0, 10)}.zip`;
                    document.body.appendChild(a);
                    a.click();
                    
                    // Nettoyer
                    setTimeout(() => {
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }, 0);
                  } catch (error: any) {
                    console.error('Erreur lors de la création du zip:', error);
                    alert(`Erreur lors du téléchargement: ${error?.message || 'Erreur inconnue'}`);
                  }
                };
                
                downloadAllImages();
              }}
              disabled={loading || results.length === 0}
            >
              Télécharger les miniatures
            </Button>
          </Box>
        )}
        
        {(loading || uploading) && <Box sx={{ mt: 2 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mt: 2 }}>{successMessage}</Alert>}
        
        {/* Affichage de l'utilisation de la bande passante */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Utilisation de la bande passante quotidienne: {bandwidthUsage.toFixed(1)}%
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={bandwidthUsage} 
            color={bandwidthUsage > 90 ? "error" : bandwidthUsage > 70 ? "warning" : "success"}
            sx={{ height: 8, borderRadius: 1 }}
          />
          {bandwidthUsage > 90 && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              Attention: La limite quotidienne de bande passante est presque atteinte.
              Les images pourraient ne plus être visibles aujourd'hui.
            </Typography>
          )}
        </Box>
        {results.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6">Images à uploader :</Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Mignature</TableCell>
                  <TableCell align="center">Nom</TableCell>
                  <TableCell align="center">Nouveau poids</TableCell>
                  <TableCell align="center">Date de prise de vue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell align="center">
                      {r.compressedUrl && (
                        <img
                          src={r.compressedUrl}
                          alt="miniature compressée"
                          style={{ maxWidth: 80, maxHeight: 80, cursor: 'pointer', border: '1px solid #444' }}
                          onClick={() => window.open(r.compressedUrl, '_blank')}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {r.compressedBlob ? `${r.file.name.replace(/\.[^.]+$/, '.jpg')}` : 'Erreur'}
                    </TableCell>
                    <TableCell align="center">
                      {r.compressedSize ? `${(r.compressedSize/1024).toFixed(1)} ko` : 'Erreur'}
                      {uploading && uploadProgress[i] !== undefined && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <Box sx={{ width: '100%', mr: 1 }}>
                            <LinearProgress variant="determinate" value={uploadProgress[i]} />
                          </Box>
                          <Box sx={{ minWidth: 35 }}>
                            <Typography variant="body2" color="text.secondary">{`${Math.round(uploadProgress[i])}%`}</Typography>
                          </Box>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {/* Afficher la date si elle existe */}
                      {r.date && (
                        <Typography variant="body2" sx={{ mb: 1, textAlign: 'center' }}>
                          {new Date(r.date).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                        </Typography>
                      )}
                      
                      {/* Afficher le sélecteur de date uniquement pour les images sans date EXIF (r.field est null) */}
                      {!r.field && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              label="Date de prise de vue"
                              value={r.manualDate ? dayjs(r.manualDate) : null}
                              onChange={(date) => {
                                if (date) {
                                  const jsDate = date.toDate();
                                  const isValid = !isNaN(jsDate.getTime()) && jsDate.getFullYear() > 1900 && jsDate.getFullYear() < 2100;
                                  if (isValid) {
                                    jsDate.setHours(23, 0, 0, 0);
                                    const newResults = [...results];
                                    newResults[i].date = jsDate.toISOString();
                                    newResults[i].manualDate = jsDate;
                                    setResults(newResults);
                                  }
                                }
                              }}
                              format="DD/MM/YYYY"
                              slotProps={{
                                textField: { 
                                  variant: 'outlined',
                                  fullWidth: true,
                                  sx: { 
                                    width: '100%', 
                                    maxWidth: '200px',
                                    '& .MuiInputLabel-root': {
                                      color: '#64b5f6',
                                      textAlign: 'center',
                                      left: 0,
                                      right: 0,
                                      margin: '0 auto',
                                      transform: 'translate(0, -6px) scale(0.75)',
                                      '&.Mui-focused, &.MuiFormLabel-filled': {
                                        transform: 'translate(0, -6px) scale(0.75)'
                                      }
                                    },
                                    '& .MuiInputBase-root': {
                                      height: '45px'
                                    },
                                    '& .MuiInputBase-input': {
                                      color: '#90caf9',
                                      textAlign: 'center',
                                      padding: '8px 14px'
                                    }
                                  }
                                },
                                actionBar: { actions: ["clear", "accept"] }
                              }}
                            />
                          </LocalizationProvider>
                        </Box>
                      )}
                    </TableCell>

                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>
      
      {/* Composant pour afficher les dates des photos orphelines */}
      <OrphanedPhotosDates title="Dates des photos orphelines (sans événement associé)" />
    </PageLayout>
  );
};

export default PhotoImport;
